// ViewGroups.js
//
// Type-scoped view group helpers (navigation buckets for saved views).

import { generateKey } from 'utilities/Keys';
import { sortViewsByConfiguredOrder } from 'utilities/ViewOrder';

export const UNSORTED_VIEW_GROUP_ID = '__unsorted__';
export const UNSORTED_VIEW_GROUP_LABEL = 'Unsorted';

/**
 * Normalizes a type's viewGroups array (stable id, label, optional description, position).
 *
 * @param {unknown} raw
 * @returns {{ id: string, label: string, description: string, position: number }[]}
 */
export function normalizeViewGroups(raw) {
  const list = Array.isArray(raw) ? raw : [];
  const seen = new Set();

  const normalized = list
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;
      const id = String(entry.id || entry.key || '').trim();
      const label = String(entry.label || entry.title || '').trim();
      if (!id || !label) return null;
      if (id === UNSORTED_VIEW_GROUP_ID) return null;
      if (seen.has(id)) return null;
      seen.add(id);
      return {
        id,
        label,
        description: String(entry.description || '').trim(),
        position: typeof entry.position === 'number' && Number.isFinite(entry.position)
          ? entry.position
          : index,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.position - b.position || a.label.localeCompare(b.label));

  return normalized.map((group, index) => ({
    ...group,
    position: index,
  }));
}

/**
 * Creates a new view group entry.
 *
 * @param {{ label?: string, description?: string, id?: string }} [input]
 * @returns {{ id: string, label: string, description: string, position: number }}
 */
export function createViewGroup(input = {}) {
  const label = String(input.label || '').trim() || 'New group';
  const id = String(input.id || '').trim() || generateKey();
  return {
    id,
    label,
    description: String(input.description || '').trim(),
    position: 0,
  };
}

/**
 * Reindexes position after reorder, preserving the given array order.
 *
 * @param {unknown} groups
 * @returns {{ id: string, label: string, description: string, position: number }[]}
 */
export function reindexViewGroups(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const seen = new Set();
  const next = [];

  list.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const id = String(entry.id || entry.key || '').trim();
    const label = String(entry.label || entry.title || '').trim();
    if (!id || !label) return;
    if (id === UNSORTED_VIEW_GROUP_ID) return;
    if (seen.has(id)) return;
    seen.add(id);
    next.push({
      id,
      label,
      description: String(entry.description || '').trim(),
      position: next.length,
    });
  });

  return next;
}

/**
 * Moves a group by id before/onto a target id (same pattern as view reorder).
 *
 * @param {unknown} groups
 * @param {string} draggedId
 * @param {string} targetId
 * @returns {{ id: string, label: string, description: string, position: number }[]|null}
 */
export function moveViewGroupById(groups, draggedId, targetId) {
  const ordered = normalizeViewGroups(groups);
  const fromId = String(draggedId || '').trim();
  const toId = String(targetId || '').trim();
  if (!fromId || !toId || fromId === toId) return null;

  const fromIndex = ordered.findIndex((group) => group.id === fromId);
  const toIndex = ordered.findIndex((group) => group.id === toId);
  if (fromIndex < 0 || toIndex < 0) return null;

  const next = [...ordered];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return reindexViewGroups(next);
}

/**
 * Buckets views into configured groups + Unsorted.
 *
 * @param {object[]} views
 * @param {unknown} viewGroups
 * @returns {{
 *   groups: Array<{ id: string, label: string, description: string, position: number, views: object[] }>,
 *   unsorted: object[],
 * }}
 */
export function bucketViewsByGroup(views, viewGroups) {
  const groups = normalizeViewGroups(viewGroups);
  const buckets = new Map(groups.map((group) => [group.id, []]));
  const unsorted = [];

  sortViewsByConfiguredOrder(views).forEach((view) => {
    const groupId = String(view?.viewGroupId || '').trim();
    if (groupId && buckets.has(groupId)) {
      buckets.get(groupId).push(view);
      return;
    }
    unsorted.push(view);
  });

  return {
    groups: groups.map((group) => ({
      ...group,
      views: buckets.get(group.id) || [],
    })),
    unsorted,
  };
}

/**
 * Whether the collection chrome should use group dropdowns.
 *
 * @param {unknown} viewGroups
 * @returns {boolean}
 */
export function hasConfiguredViewGroups(viewGroups) {
  return normalizeViewGroups(viewGroups).length > 0;
}

/**
 * Resolves which group id currently owns the active view (including unsorted).
 *
 * @param {object[]} views
 * @param {unknown} viewGroups
 * @param {string|null|undefined} activeViewId
 * @returns {string|null}
 */
export function resolveActiveViewGroupId(views, viewGroups, activeViewId) {
  const activeId = String(activeViewId || '').trim();
  if (!activeId) return null;
  const { groups, unsorted } = bucketViewsByGroup(views, viewGroups);
  for (const group of groups) {
    if (group.views.some((view) => String(view?.key || view?.id || '').trim() === activeId)) {
      return group.id;
    }
  }
  if (unsorted.some((view) => String(view?.key || view?.id || '').trim() === activeId)) {
    return UNSORTED_VIEW_GROUP_ID;
  }
  return null;
}
